import { handleServiceError } from './errors/errorHandler';

const RELOAD_FLAG_KEY = 'chunk_load_error_reload_attempted';
const MAX_RELOAD_ATTEMPTS = 1;

/**
 * Detects if an error is related to chunk loading failures
 * Common patterns during deployments when old chunks are no longer available
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  const chunkErrorPatterns = [
    'Failed to fetch dynamically imported module',
    'Loading chunk',
    'Loading CSS chunk failed',
    'error loading dynamically imported module',
    'ChunkLoadError',
    'dynamically imported module'
  ];
  
  return chunkErrorPatterns.some(pattern => 
    errorMessage.toLowerCase().includes(pattern.toLowerCase())
  );
};

/**
 * Handles chunk load errors by attempting an automatic page reload
 * Includes loop prevention to avoid infinite reloads
 */
export const handleChunkLoadError = async (error: unknown): Promise<boolean> => {
  if (!isChunkLoadError(error)) {
    return false;
  }

  // Check if we've already attempted a reload
  const reloadAttempts = parseInt(
    sessionStorage.getItem(RELOAD_FLAG_KEY) || '0',
    10
  );

  if (reloadAttempts >= MAX_RELOAD_ATTEMPTS) {
    console.warn('[ChunkLoadError] Max reload attempts reached, showing error UI');
    
    // Log to audit logs for monitoring
    await handleServiceError(error, {
      action: 'chunk_load_error_max_attempts',
      component: 'ChunkLoadErrorHandler'
    });
    
    return false;
  }

  // Increment reload counter
  sessionStorage.setItem(RELOAD_FLAG_KEY, String(reloadAttempts + 1));

  console.info('[ChunkLoadError] Detected chunk loading error, reloading page...', {
    error: error instanceof Error ? error.message : String(error),
    attempt: reloadAttempts + 1
  });

  // Log to audit logs
  await handleServiceError(error, {
    action: 'chunk_load_error_auto_reload',
    component: 'ChunkLoadErrorHandler'
  });

  // Reload the page to fetch fresh chunks
  window.location.reload();
  
  return true;
};

/**
 * Resets the reload counter (useful for testing or manual recovery)
 */
export const resetChunkLoadErrorFlag = (): void => {
  sessionStorage.removeItem(RELOAD_FLAG_KEY);
};

/**
 * Hook to reset the flag when the app loads successfully
 * Call this in your main App component
 */
export const useChunkLoadErrorReset = (): void => {
  // Reset the flag on successful app load
  // This ensures we can handle future chunk errors
  if (typeof window !== 'undefined') {
    // Use a timeout to ensure the app has fully loaded
    setTimeout(() => {
      resetChunkLoadErrorFlag();
    }, 1000);
  }
};
